import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Typography } from 'antd';
import cn from 'classnames';

import companyLogo from 'assets/images/company-logo.png';
import SharedIcon from 'assets/images/icons/shared.svg?react';
import { TaskContractorRead } from 'shared/api/client/codegen.api';
import { EMainRoutes } from 'shared/typings';
import { CopyButton } from 'shared/ui/CopyButton/CopyButton';
import { formatDate, formatNumber } from 'shared/utils';

import styles from './OfferCard.module.scss';

interface IOfferCardProps extends HTMLAttributes<HTMLDivElement> {
	deadline?: string;
	publishedAt?: string;
	price?: string;
	primaryBtnText?: string;
	secondaryBtnText?: string;
	contractor?: TaskContractorRead;
	isShared?: boolean;
	id?: string;
	onPrimaryAction?: () => void;
	onSecondaryAction?: () => void;
}

export const OfferCard: FC<IOfferCardProps> = ({
	className,
	contractor,
	price,
	deadline,
	publishedAt,
	onPrimaryAction,
	onSecondaryAction,
	primaryBtnText,
	secondaryBtnText,
	isShared,
	id,
	...props
}) => {
	const { t } = useTranslation();
	const link = id ? `${location.origin}/${EMainRoutes.PUBLIC}/${EMainRoutes.OFFER}/${id}` : '';

	return (
		<Flex
			{...props}
			className={cn(className, styles.card)}
			vertical
			gap={12}
			style={{ width: '100%' }}
			justify="space-between">
			<img src={companyLogo} width={100} height={44} alt="Company" />
			<Flex vertical align={'start'}>
				<Typography.Title level={5} style={{ margin: '0' }}>
					{t('STREET BEAT')}
				</Typography.Title>
				<Typography.Text type="secondary">
					{contractor ? `${t('Proposal for')} @${contractor.username}` : t('Public offer terms')}
				</Typography.Text>
			</Flex>
			<dl className={styles.table}>
				<dt className={styles.dt}>
					<Typography.Text type="secondary">{t('Publication date:')}</Typography.Text>
				</dt>
				<dd className={styles.dd}>
					<Typography.Text type="secondary">
						{formatDate(publishedAt ? new Date(publishedAt) : new Date(), 'shortNumeric')}
					</Typography.Text>
				</dd>
				<dt className={styles.dt}>
					<Typography.Text type="secondary">{t('Deadline:')}</Typography.Text>
				</dt>
				<dd className={styles.dd}>
					<Typography.Text type="secondary">{formatDate(new Date(deadline || ''), 'shortNumeric')}</Typography.Text>
				</dd>
				<dt className={styles.dt}>
					<Typography.Text type="secondary">{t('Price:')}</Typography.Text>
				</dt>
				<dd className={styles.dd}>
					<Typography.Text>$ {formatNumber(price || '')}</Typography.Text>
				</dd>
			</dl>
			{onPrimaryAction && (
				<Button type="primary" onClick={() => onPrimaryAction?.()}>
					{primaryBtnText || t('Edit offer')}
				</Button>
			)}
			{onSecondaryAction && (
				<Button
					type="primary"
					ghost
					icon={secondaryBtnText ? null : <SharedIcon />}
					iconPlacement="start"
					onClick={() => onSecondaryAction?.()}>
					{secondaryBtnText}
				</Button>
			)}
			{isShared && (
				<CopyButton
					type="primary"
					ghost
					icon={<SharedIcon />}
					iconPlacement="start"
					textMode
					tooltipText={t('Share a link')}
					text={link}
				/>
			)}
		</Flex>
	);
};
